import { useState, useCallback } from 'react';
import { Flex, Button } from 'components/ui';
import { ERC20Token } from './common';
import { useApproveToken } from 'hooks/useApproveToken';
import { notifyTx } from 'utils/notifications';
import { isDev } from 'utils/config';
import { useQueryClient } from 'react-query';
import { useWallet } from './WalletContext';

interface ApproveTokenProps {
  children: JSX.Element;
  amount: string;
  spendingContractAddress: string;
  paymentToken: ERC20Token;
  approveLabel: string;
  chainId: number;
  useExactAmount?: boolean;
}

const ApproveERC20 = ({
  children,
  amount,
  paymentToken,
  spendingContractAddress,
  approveLabel,
  chainId,
  useExactAmount,
}: ApproveTokenProps) => {
  const { isApproved, approve, removeApproval } = useApproveToken(
    paymentToken,
    amount,
    spendingContractAddress,
    chainId,
    useExactAmount ?? false
  );
  const { address } = useWallet();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries(['approval', paymentToken.address, address]);
    setPending(false);
  }, [queryClient, paymentToken.address, address]);

  const onApprove = useCallback(async () => {
    notifyTx({
      method: approve,
      chainId,
      success: 'ETH Approved.',
      error: 'An error occured while trying to approve ETH.',
      onSubmitted: () => setPending(true),
      onError: () => setPending(false),
      onSuccess,
    });
  }, [chainId, approve, onSuccess]);

  const onRemoveApproval = useCallback(async () => {
    notifyTx({
      method: removeApproval,
      chainId,
      success: 'Approval removed',
      onSubmitted: () => setPending(true),
      onError: () => setPending(false),
      onSuccess,
      error: 'An error occured while trying to remove approval for ETH.',
    });
  }, [chainId, removeApproval, onSuccess]);

  return (
    <>
      {isApproved ? (
        <Flex>
          {children}
          {isDev() && (
            <Button type="button" disabled={pending} ml={2} onClick={onRemoveApproval}>
              [DEV] Revoke approval
            </Button>
          )}
        </Flex>
      ) : (
        <Button type="button" disabled={pending} onClick={onApprove}>
          {approveLabel}
        </Button>
      )}
    </>
  );
};

export default ApproveERC20;
